import React from 'react'

import config from 'environment'

import ContextMenu from 'components/elements/ContextMenu'
import { createKeybindLabel } from 'components/elements/ArchidektDropdown'
import { Option } from 'components/elements/ArchidektDropdown/types'
import { DeckCardWrapperChildProps } from './deckCardWrapper'

import { creatEdhrecCardUrl } from 'utils/edhrec.utils'

import styles from './deckCardContextMenu.module.scss'
import { getPriceStringAndUrl } from 'utils/Price'
import { PRICE_SOURCE } from 'types/active'
import { OWNED } from 'types/deck'
import { commanderNameToUrl } from 'utils/commanderNameAliases'
import { checkIsCommander } from 'utils/isCommander'

type Props = {
  children?: React.ReactNode
  checkSkipRightClick: () => boolean
  wrapperData: DeckCardWrapperChildProps
}

const DeckCardContextMenu = ({ children, wrapperData, checkSkipRightClick }: Props) => (
  <ContextMenu
    checkSkipRightClick={checkSkipRightClick}
    options={generateContextOptions(wrapperData, true)}
    menuClassName={styles.contextMenu}>
    {children}
  </ContextMenu>
)

export default DeckCardContextMenu

export const generateContextOptions = (
  { card, actions, localCardData, deckData, userData, extraData }: DeckCardWrapperChildProps,
  asContextMenu?: boolean,
): Option[] => {
  const { url: tcgUrl } = getPriceStringAndUrl(card, PRICE_SOURCE.TCG, card.modifier !== 'Normal')
  const { url: ckUrl } = getPriceStringAndUrl(card, PRICE_SOURCE.CK, card.modifier !== 'Normal')

  const autoCategory = card.categories[1] || card.types[0] || card.front?.types[0] || '😎'
  const isCommander = checkIsCommander(card)

  const writeAccessActions: Option[] = [
    {
      label: createKeybindLabel('Increase quantity', 'add', '+'),
      onClick: actions.incrementCard,
      noCloseOnClick: true,
    },
    {
      label: createKeybindLabel('Decrease quantity', 'minus', '-'),
      onClick: actions.decrementCard,
      disabled: !localCardData.quantity,
      noCloseOnClick: true,
    },
    {
      icon: 'circle-plus',
      label: 'Add as new card',
      onClick: actions.addAsNewCard,
      disabled: !localCardData.quantity,
    },
    {
      icon: 'pin',
      label: card.pinnedStatus === OWNED.NO ? 'Pin card' : 'View pinned card',
      onClick: () => actions.addToArchidektPins(),
      hidden: !!card.customCard?.id,
      keybind: 'W',
    },
    { type: 'spacer' },
    { label: 'Switch card printing', onClick: () => actions.openCardModal(true), icon: 'grid layout', keybind: 'P' },
    {
      label: createKeybindLabel('Switch to nonfoil', 'star outline', 'N'),
      onClick: actions.setNormal,
      hidden: !localCardData.hasFinishOptions || localCardData.isNormal,
    },
    {
      label: createKeybindLabel('Switch to foil', 'star', 'F', 'blue'),
      onClick: actions.setFoil,
      hidden: !localCardData.hasFinishOptions || localCardData.isFoil,
    },
    {
      label: createKeybindLabel('Switch to etched', 'star', 'E', 'orange'),
      onClick: actions.setEtched,
      hidden: !localCardData.hasEtchedOption || !localCardData.hasFinishOptions || localCardData.isEtched,
    },
    {
      label: 'Set as commander',
      icon: 'chess queen',
      hidden: deckData.categories[card.categories[0]]?.isPremier,
      // NOTE - we're setting the quantity to 1 here so we can forcibly add the card to the deck if it's not yet in the deck (from card search)
      // Matching the other set commander buttons, remove all other categories and just set 'Commander'
      onClick: () => actions.onCardUpdate({ ...card, qty: 1 || card.qty, categories: ['Commander'] }),
    },
    {
      label: 'Unset commander',
      icon: 'ban',
      hidden: !deckData.categories[card.categories[0]]?.isPremier,
      onClick: () =>
        actions.onCardUpdate({
          ...card,
          categories:
            card.categories.length > 1 ? [...card.categories.filter((_card, index) => index > 0)] : [card.typeCategory],
        }),
    },
    { type: 'spacer' },
    {
      type: 'extras',
      label: 'Move to category',
      icon: 'arrow right',
      id: 'move-to-category',
      className: styles.categoryMenu,
      options: [
        {
          label: createKeybindLabel('Automatic category', undefined, 'A'),
          onClick: () => actions.changeCategory(autoCategory),
          disabled: card.categories[0] === autoCategory,
        },
        {
          label: createKeybindLabel('Maybeboard', undefined, 'M'),
          onClick: () => actions.changeCategory('Maybeboard'),
          disabled: card.categories[0] === 'Maybeboard',
        },
        {
          label: createKeybindLabel('Sideboard', undefined, 'S'),
          onClick: () => actions.changeCategory('Sideboard'),
          disabled: card.categories[0] === 'Sideboard',
        },
        { type: 'spacer' },
        { icon: 'circle-plus', label: 'Create new category', onClick: actions.makeNewCategory },
        { type: 'spacer' },
        ...localCardData.categoryNames
          .filter(categoryName => !['Sideboard', 'Maybeboard', 'Commander'].includes(categoryName))
          .map(categoryName => ({
            label: categoryName,
            onClick: () => actions.changeCategory(categoryName),
          })),
      ],
    },

    { type: 'spacer' },
  ]

  return [
    { type: 'header', label: card.name },
    { type: 'spacer' },
    { label: 'Open details', onClick: actions.openCardModal, icon: 'arrow right', keybind: 'Click' },
    { type: 'spacer' },
    {
      label: createKeybindLabel('Flip card', 'refresh', 'L'),
      onClick: () => actions.setFlipped(!localCardData.flipped),
      hidden: !localCardData.isFlipCard,
      noCloseOnClick: true,
    },
    { type: 'spacer', hidden: !localCardData.isFlipCard },

    ...(localCardData.ownsDeck ? writeAccessActions : []),

    {
      label: createKeybindLabel('Remove card', 'trash alternate', 'R'),
      onClick: actions.removeCard,
      hidden: !localCardData.ownsDeck || card.qty < 1,
    },
    {
      label: createKeybindLabel(
        'Multi-select',
        'hand point up outline',
        config.getOnMac() ? 'Cmd+Click' : 'Ctrl + Click',
      ),
      onClick: actions.multiSelect,
      hidden: extraData.noMultiSelect,
    },
    {
      label: createKeybindLabel('Compare card with...', 'eye'), // redundant from above option, different wording for people who want to compare cards
      onClick: actions.multiSelect,
      hidden: extraData.noMultiSelect,
    },
    {
      label: 'View card page',
      icon: 'external alternate',
      to: `/card?uid=${card.uid}`,
      hidden: localCardData.ownsDeck,
    },
    { label: 'Copy card name', icon: 'copy', onClick: actions.copyCardName, hidden: localCardData.ownsDeck },
    { type: 'spacer', hidden: !card.customCard?.id || localCardData.ownsDeck || !userData.id },
    {
      label: 'Report custom card',
      onClick: () => actions.reportCard(),
      hidden: !card.customCard?.id || localCardData.ownsDeck || !userData.id,
      icon: 'flag outline',
    },
    { type: 'spacer', hidden: !localCardData.ownsDeck },
    {
      type: 'extras',
      id: 'extras-menu',
      icon: 'arrow right',
      label: 'Card extras',
      hidden: !localCardData.ownsDeck,
      options: [
        { label: 'View card page', icon: 'external alternate', to: `/card?uid=${card.uid}` },
        { label: 'Copy card name', icon: 'copy', onClick: actions.copyCardName, keybind: 'C' },
        { type: 'spacer' },
        {
          label: 'View on EDHREC',
          target: '_blank',
          to: creatEdhrecCardUrl(card),
          customIcon: 'edhrec',
        },
        {
          label: 'View on Scryfall',
          target: '_blank',
          to: `https://scryfall.com/card/${card.uid}`,
          icon: 'code',
        },
        {
          label: 'View on TCGPlayer',
          target: '_blank',
          to: tcgUrl,
          customIcon: 'tcg',
        },
        {
          label: 'View on Card Kingdom',
          target: '_blank',
          to: ckUrl,
          customIcon: 'cardKingdom',
        },
        { type: 'spacer' },
        {
          to: `/commanders/${commanderNameToUrl(card.name)}`,
          label: 'Matching commanders',
          target: '_blank',
          hidden: !isCommander,
          icon: 'chess king',
        },
        {
          to: `/search/decks/?cardName=${encodeURIComponent(card.name)}`,
          label: 'View other decks',
          icon: 'search',
          target: '_blank',
        },
      ],
    },
    {
      icon: 'pin',
      label: card.pinnedStatus === OWNED.NO ? 'Pin card' : 'View pinned card',
      onClick: () => actions.addToArchidektPins(),
      hidden: localCardData.ownsDeck || !!card.customCard?.id || !userData.id,
      keybind: 'W',
    },
    { type: 'message', label: 'Ctrl + Right Click for standard menu', hidden: !asContextMenu },
  ]
}
